import { useCustom } from "@refinedev/core";
import { AlertTriangle, ArrowLeft, Barcode, QrCode } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { CodeSheet, type CodeRequest } from "@/components/CodeSheet";
import { cancelVerdict } from "@/lib/cancel";
import { errorMessage, thingWord } from "@/lib/format";
import { pageData } from "@/lib/loaded";
import type { FbsBoxCard, FbsUnit } from "@/lib/providers";
import { taskHref } from "@/lib/task";
import { useSessionGuard } from "@/lib/useSessionGuard";

import { Cell, Empty, SupplyCell } from "./FbsBoxesPage";

/**
 * Карточка короба отгрузки: что в нём числится и что с этим у маркетплейса.
 *
 * Список отвечает «сколько за нами числится», карточка — «что именно»:
 * каждая строка это ОДНА ВЕЩЬ с её номером серии, заказом и состоянием.
 * Ради этих номеров карточка и открывается: несписанную вещь ищут по серии,
 * а лежащую в коробе на самом деле — достают руками.
 *
 * Код места здесь РИСУЕТСЯ QR-ом, а баркод вещи — Code 128: у человека,
 * разбирающего короб, сканер уже в руке, и набирать цифры глазами —
 * единственное, чего он делать не должен (тот же приём, что в «Коробах»).
 */
export function FbsBoxPage() {
  const { placeId = "" } = useParams();
  // Какой код показан во весь экран. `null` — ни одного.
  const [code, setCode] = useState<CodeRequest | null>(null);

  const boxQuery = useCustom<FbsBoxCard>({
    url: `/api/fbs/boxes/${encodeURIComponent(placeId)}`,
    method: "get",
  });
  useSessionGuard(boxQuery.query.error);

  // Коробом считается только payload со списком вещей: `result.data` у Refine
  // существует и до загрузки — пустым объектом (см. `lib/loaded.ts`).
  const box = pageData(boxQuery.result.data, "rows");
  const error = boxQuery.query.error;

  if (error && !box)
    return (
      <Shell>
        <p role="alert" className="text-[13px] text-err">
          {errorMessage(error)}
        </p>
      </Shell>
    );

  if (!box)
    return (
      <Shell>
        <p className="text-[13px] text-fg-muted">Открываю короб…</p>
      </Shell>
    );

  const known = box.neighbour.units;
  const shipped = box.shipped ?? 0;
  const here = box.here ?? 0;

  return (
    <div
      className="flex h-full min-h-0 flex-col p-4"
      data-component="FbsBoxPage"
      data-annot={`fbs короб ${box.code}`}
    >
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link
            to={`/fbs?platform=${box.platform}`}
            className="mb-1 inline-flex items-center gap-1 text-[12px] text-fg-muted hover:text-fg"
          >
            <ArrowLeft size={12} />К коробам {box.platform_name}
          </Link>
          <h1 className="text-[15px] font-semibold">
            {box.name || box.code}
            <span className="ml-2 text-[13px] font-normal text-fg-muted">
              {box.city}
            </span>
          </h1>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[12.5px] text-fg-muted">
            {/* Код места открывает QR: в CRM короб находят СКАНОМ МЕСТА, а не
                его номером. */}
            {box.place_barcode ? (
              <button
                type="button"
                onClick={() =>
                  setCode({
                    kind: "qr",
                    value: box.place_barcode,
                    title: `Код места · ${box.name || box.code}`,
                  })
                }
                title="Показать QR с кодом места"
                data-annot="код места"
                className="-mx-1 inline-flex items-center gap-1.5 rounded-[var(--radius-ui)] px-1 py-0.5 tnum hover:bg-bg-hover"
              >
                {box.place_barcode}
                <QrCode size={13} className="flex-none" />
              </button>
            ) : (
              <span>без кода места</span>
            )}
          </p>
        </div>

        <dl data-annot="сводка" className="flex flex-wrap gap-2">
          <Metric label="Штук" value={box.units} />
          {/* Главное число карточки: вещи, которых физически нет, а в учёте
              они наши. Прочерк, а не ноль, когда базы задач соседа нет: ноль
              читался бы как «всё списано». */}
          <Metric
            label="Ждут списания"
            value={known ? shipped : "—"}
            note={known ? `${thingWord(shipped)} у маркетплейса` : "не посчитано"}
            accent
          />
          <Metric
            label="Ещё у нас"
            value={known ? here : "—"}
            note={known ? "лежат в коробе" : "не посчитано"}
          />
        </dl>
      </div>

      {/* Документ отгрузки короба — общий на весь короб, поэтому стоит над
          таблицей, а не колонкой в каждой строке. */}
      <div
        data-annot="отгрузка"
        className="mb-3 flex flex-wrap items-center gap-2 rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-2 text-[12.5px]"
      >
        <span className="text-fg-muted">Отгрузка:</span>
        <SupplyCell supply={box.supply} />
        {!box.neighbour.supply ? (
          <span className="text-fg-muted">
            · кабинет {box.platform_name} сейчас недоступен
          </span>
        ) : null}
      </div>

      {box.rows.length === 0 ? (
        <Empty>Короб разобран — за ним ничего не числится.</Empty>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
          <table
            data-annot="таблица"
            className="w-full border-collapse text-[13px]"
          >
            <caption className="sr-only">
              Вещи короба {box.name || box.code} с их заказами и состоянием
            </caption>
            <thead className="hidden md:table-header-group">
              <tr>
                {/* Все колонки слева, включая «Состояние». Раньше оно стояло
                    справа одним словом («у нас»), а теперь несёт разбор отмены
                    в две-три строки: прижатый вправо абзац рвал бы взгляд на
                    каждой строке, да и подпись колонки разошлась бы с ним. */}
                {["Серия", "Задача", "Товар", "Баркод", "Заказ", "Состояние"].map(
                  (title) => (
                    <th
                      key={title}
                      scope="col"
                      className="sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated px-2.5 py-2 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-fg"
                    >
                      {title}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="block md:table-row-group">
              {box.rows.map((row) => (
                <tr
                  key={row.serial}
                  // Адрес замечания — НОМЕР СЕРИИ: он единственный, что
                  // находится в CRM и не зависит от порядка строк.
                  data-annot={`строка ${row.serial}`}
                  className="mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover"
                >
                  <Cell label="Серия">
                    {/* Карточка серии отвечает на «что с ЭТОЙ вещью» — куда
                        она переезжала и списана ли. Отсюда туда и ходят. */}
                    <Link
                      to={`/serial/${row.serial}`}
                      className="font-medium tnum text-fg hover:text-accent-text"
                    >
                      {row.serial}
                    </Link>
                  </Cell>
                  {/* Задача владельца 09.09.26: строка короба говорила, что с
                      вещью у маркетплейса, и молчала о том, какая РАБОТА
                      СКЛАДА за ней стоит. По серии кликнуть было можно, по
                      задаче — нет, а разбираться «почему поставка закрыта, а
                      мы не списываем» идут именно в задачу. */}
                  <Cell label="Задача">
                    {row.task_id ? (
                      <Link
                        to={taskHref(row.task_id)}
                        className="tnum text-accent-text underline decoration-dotted underline-offset-2 hover:no-underline"
                      >
                        {row.task_id}
                      </Link>
                    ) : (
                      <span
                        className="text-[12px] text-fg-muted"
                        title="Складской задачи за этой вещью нет: в короб её положили руками"
                      >
                        руками
                      </span>
                    )}
                  </Cell>
                  <Cell label="Товар">
                    <span className="flex flex-col">
                      <span>{row.name || "без карточки маркетплейса"}</span>
                      {row.size ? (
                        <span className="text-[11px] text-fg-muted">
                          размер {row.size}
                        </span>
                      ) : null}
                    </span>
                  </Cell>
                  <Cell label="Баркод">
                    {row.barcode ? (
                      <button
                        type="button"
                        onClick={() =>
                          setCode({
                            kind: "barcode",
                            value: row.barcode,
                            title: `Баркод · серия ${row.serial}`,
                          })
                        }
                        title="Показать Code 128"
                        className="-mx-1 inline-flex items-center gap-1.5 rounded-[var(--radius-ui)] px-1 py-0.5 tnum hover:bg-bg-hover"
                      >
                        {row.barcode}
                        <Barcode size={13} className="flex-none" />
                      </button>
                    ) : (
                      <span className="text-fg-muted">—</span>
                    )}
                  </Cell>
                  <Cell label="Заказ">
                    <span className="flex flex-col">
                      <span className="tnum text-[12.5px]">
                        {row.document || "—"}
                      </span>
                      {row.status ? (
                        <span className="text-[11px] text-fg-muted">
                          {row.status}
                        </span>
                      ) : null}
                    </span>
                  </Cell>
                  <Cell label="Состояние">
                    <State row={row} known={known} />
                  </Cell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {code ? <CodeSheet request={code} onClose={() => setCode(null)} /> : null}
    </div>
  );
}

/**
 * Что с вещью: уже у маркетплейса или ещё у нас — и ПОЧЕМУ.
 *
 * Два состояния — две разные работы, и подписаны они словами, а не галочкой:
 * «ждёт списания» разберёт автомат, «у нас» придётся достать руками. Без
 * базы задач соседа состояние неизвестно — тогда прочерк, а не «у нас»:
 * молчание честнее выдуманного ответа.
 *
 * Под состоянием стоит разбор отмены (вопрос владельца 09.09.26): «отменено»
 * само по себе не говорит, где вещь, потому что Ozon отменяет заказ и ПОСЛЕ
 * того, как посылку сдали. Слова «до отгрузки» и «после отгрузки» — это «вещь
 * в коробе» против «вещи там нет», и различить их можно только так.
 */
function State({ row, known }: { row: FbsUnit; known: boolean }) {
  if (!known) return <span className="text-[12px] text-fg-muted">—</span>;
  // Признак «уехало» у Ozon — акт перевозки, и он же довод разбора отмены:
  // второго ответа на «уехала ли вещь» у экрана нет и быть не должно.
  const verdict = cancelVerdict(row.cancel, row.shipped);
  return (
    <span className="flex flex-col items-end gap-0.5 md:items-start">
      {row.shipped ? (
        <span className="text-[12.5px] font-medium text-accent-text">
          ждёт списания
        </span>
      ) : (
        <span className="text-[12.5px] text-warn">у нас</span>
      )}
      {verdict ? (
        <span
          className="flex flex-col items-end text-right md:items-start md:text-left"
          title={verdict.note}
        >
          <span
            className={
              "flex items-center gap-1 text-[11.5px] " +
              (verdict.disputed ? "font-medium text-err" : "text-fg-muted")
            }
          >
            {verdict.disputed ? (
              <AlertTriangle size={11} className="flex-none" />
            ) : null}
            {verdict.label}
          </span>
          <span className="text-[11px] text-fg-muted">{verdict.note}</span>
        </span>
      ) : null}
    </span>
  );
}

/** Плитка сводки карточки: подпись, число и пояснение под ним. */
function Metric({
  label,
  value,
  note,
  accent,
}: {
  label: string;
  value: string | number;
  note?: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-2">
      <dt className="text-[11px] uppercase tracking-[0.04em] text-fg-muted">
        {label}
      </dt>
      <dd
        className={
          "text-[19px] font-semibold tabular-nums tracking-[-0.02em] " +
          (accent ? "text-accent-text" : "")
        }
      >
        {value}
      </dd>
      {note ? <p className="text-[11px] text-fg-muted">{note}</p> : null}
    </div>
  );
}

/** Каркас на время загрузки и на случай отказа — с той же ссылкой назад. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-4" data-component="FbsBoxPage" data-annot="fbs короб">
      <Link
        to="/fbs"
        className="mb-2 inline-flex items-center gap-1 text-[12px] text-fg-muted hover:text-fg"
      >
        <ArrowLeft size={12} />К коробам FBS
      </Link>
      {children}
    </div>
  );
}
